import type { LanguageCode } from "@@/shared/translations/language-code";
import type { TranslatableValue } from "@@/shared/translations/translation";
import { translation } from "@@/shared/translations/translation";
import {
    getLongDateWeekDayMonth,
    getShortDateMonth,
    getShortDateWeekDayMonth,
} from "@@/utilities/date-and-time/name-of-day";
import { getTimeOfDay } from "@@/utilities/date-and-time/time-of-day";
import { toZonedTime } from "date-fns-tz";
import type { Except } from "type-fest";
import { asDate } from "./as-date";
import { isSameDayInTimezone } from "./is-same-day-in-timezone";
import type { Time } from "./time";
import { timeToString } from "./time";
import type { TimeGranularity } from "./time-granularity";
import { isTimeRange } from "./time-range";
import { isTimeSpot } from "./time-spot";
import type { Timezone } from "./timezone";

type Params = {
    time: Time | undefined;
    /**
     * defaults to `HOUR`
     * @type {TimeGranularity}
     */
    granularity?: TimeGranularity;
    /**
     * If true, don't show end for time range
     * @type {boolean}
     */
    onlyStartTime?: boolean;

    longDate?: boolean;
    // If true, don't show weekday
    dontShowWeekday?: boolean;

    currentTimezoneName: Timezone;
    languageCode?: LanguageCode;
};
/**
 * If time is undefined, function returns an empty string
 */
function formatBookedTime(params: Params): string {
    const languageCode = params.languageCode ?? "sv";
    const { time, currentTimezoneName } = params;
    if (!time) return "";
    const granularity = params.granularity ?? "HOUR";
    const onlyStartTime = !!params.onlyStartTime;
    const start = isTimeRange(time) || isTimeSpot(time) ? time.start : time;

    const end = isTimeRange(time) ? time.end : start;

    const zonedStart = toZonedTime(asDate(start), currentTimezoneName);
    const zonedEnd = toZonedTime(asDate(end), currentTimezoneName);

    const dateFunction = params.longDate
        ? getLongDateWeekDayMonth
        : params.dontShowWeekday
          ? getShortDateMonth
          : getShortDateWeekDayMonth;

    const startWeekdayDateAndMonth = dateFunction(
        zonedStart, // asDate(start),
        languageCode,
    ).toLowerCase();
    const endWeekdayDateAndMonth = dateFunction(zonedEnd, languageCode);

    if (
        onlyStartTime ||
        isSameDayInTimezone(asDate(start), asDate(end), currentTimezoneName)
    ) {
        return granularity === "DAY" || granularity === "NIGHT"
            ? startWeekdayDateAndMonth
            : `${startWeekdayDateAndMonth} ${timeToString(time, {
                  onlyStartTime,
              })}`;
    } else {
        return granularity === "DAY" || granularity === "NIGHT"
            ? `${startWeekdayDateAndMonth} - ${endWeekdayDateAndMonth}`
            : `${startWeekdayDateAndMonth} ${getTimeOfDay(
                  start,
              )} - ${endWeekdayDateAndMonth} ${getTimeOfDay(end)}`;
    }
}

const formatBookedTimeTranslated = (
    params: Except<Params, "languageCode">,
): TranslatableValue => {
    return translation({
        sv: formatBookedTime({ ...params, languageCode: "sv" }),
        en: formatBookedTime({ ...params, languageCode: "en" }),
        de: formatBookedTime({ ...params, languageCode: "de" }),
    });
};
export { formatBookedTime, formatBookedTimeTranslated };
